/**
 * Every tuning constant of the floor/storey detection pipeline, in one place.
 *
 * These are first-guess values expected to be re-tuned against real scans —
 * synthetic fixtures have repeatedly misled tuning in this codebase, so treat
 * real multi-storey captures as the only ground truth. Tests pass explicit
 * values so retuning never rewrites tests.
 */

export interface FloorDetectionTuning {
  // -- grid --
  /**
   * XY column size (m). Fine enough to separate rooms across a 10–15 cm wall
   * and resolve MIN_PATCH_AREA_M2 patches; coarse enough that a 20×20 m
   * selection is ~6.4k columns.
   */
  CELL_SIZE_XY: number;
  /**
   * Z histogram bin (m). Half a typical slab thickness, so slab top and
   * bottom land in distinct bins.
   */
  BIN_SIZE_Z: number;
  /** Column-count guard (512²): cellSize grows on huge selections so memory/time stay bounded. */
  MAX_COLUMNS: number;
  /** Bin-count guard (~200 m z-range at 5 cm): binSize grows beyond it. */
  MAX_BINS: number;

  // -- surface extraction --
  /**
   * Minimum raw points in the peak ± 1 bin window. A real floor in one column
   * has dozens at working LODs; this rejects stray noise yet survives thin
   * far-field coverage.
   */
  MIN_SURFACE_POINTS: number;
  /**
   * A peak's CENTER bin must exceed this multiple of the column's background
   * occupancy (median over all bins — 0 for mostly-empty room columns). Wall
   * columns are occupied over their whole height, so their local maxima fail
   * this and the column yields no surfaces (by design — walls need no
   * intervals).
   */
  SURFACE_PROMINENCE_FACTOR: number;
  /** Local maxima closer than this (bins) are one physical surface split by noise — merged, keeping the stronger. */
  MIN_PEAK_SEPARATION_BINS: number;

  // -- interval pairing --
  /** Below code-legal room height; also above any slab thickness, so slab faces never pair as a room. */
  MIN_ROOM_HEIGHT: number;
  /** Above this we assume a missed intermediate floor (atria still resolve via neighboring columns). */
  MAX_ROOM_HEIGHT: number;
  /** Metres above the floor surface ignored by the emptiness test — tables, sofas, counters live here. */
  FURNITURE_IGNORE_HEIGHT: number;
  /** Metres below the ceiling ignored — lamps, beams, ducts, curtain rails. */
  CEILING_CLEARANCE: number;
  /** Absolute stray points tolerated in the gap zone (scanner noise floor). */
  GAP_MIN_POINT_BUDGET: number;
  /** …or this fraction of (floorCount + ceilingCount), whichever is larger — density-adaptive. */
  GAP_FRACTION: number;
  /**
   * A surface inside the gap zone with at least this fraction of
   * min(floorCount, ceilingCount) vetoes the pairing — there is a real floor
   * between the candidates.
   */
  INTERMEDIATE_SURFACE_FACTOR: number;
  /**
   * A floor/ceiling CANDIDATE must carry at least this fraction of the
   * column's strongest surface's points. Weak surfaces (wardrobe tops, door
   * frames, kitchen soffits) stay in the column — they still veto pairings
   * as intermediate surfaces — but can no longer BE a room's floor or
   * ceiling while a clearly stronger surface exists in the same column.
   * Relative to the column's own max, so LOD/density changes don't shift it.
   */
  CANDIDATE_STRENGTH_FACTOR: number;
  /** A later floor candidate must sit above the last accepted ceiling by at least this (skips furniture inside an accepted room). */
  MIN_SLAB_SEPARATION: number;

  // -- room patches --
  /**
   * Two neighboring column intervals connect when their z-overlap divided by
   * the LARGER interval height reaches this. Using the larger height keeps
   * gently sloping ceilings connected but keeps a stairwell/void column (tall
   * interval) and a mezzanine (nested short interval) out of normal-height
   * patches.
   */
  INTERVAL_OVERLAP_MIN: number;
  /**
   * Patches below this area are noise. Sized just under a toilet stall
   * (~1.1 m²); also small enough that a point-roof attic — where only the
   * ridge strip clears MIN_ROOM_HEIGHT and single blocked columns split it —
   * keeps its fragments (real scan: 20+23 cell pieces at 0.25 m cells).
   */
  MIN_PATCH_AREA_M2: number;
  /** Fraction trimmed from EACH side for robust patch floor/ceiling averages. */
  TRIM_FRACTION: number;
  /** p90 − p10 of a patch's ceiling samples beyond this flags a sloped/vaulted ceiling. */
  SLOPED_CEILING_SPREAD: number;

  // -- storeys --
  /**
   * Patches whose floorZ differs no more than this join one storey band;
   * also the window for splitting staircase-smeared patches into floor
   * bands. Merges slab-flatness jitter (a real four-storey scan shows ≤5 cm
   * within a storey) yet separates genuinely distinct floor areas — the same
   * scan has a sunken ground area 10.6 cm below the main ground floor that
   * must stay its own level (fixture 2026-09-08-four-storeys). A patch
   * outside every band becomes its own proposed level.
   */
  STOREY_FLOOR_TOLERANCE: number;
  /** Slab gap (next floor − ceiling) below this is suspicious: both surfaces likely belong to the same slab. */
  MIN_SLAB_GAP: number;
  /** Slab gap above this suggests a missed technical/void level. Flags, never drops. */
  MAX_SLAB_GAP: number;
  /**
   * A storey with less than this fraction of the largest storey's area, whose
   * z-range nests inside another storey, is flagged `isSubLevel` (mezzanine).
   */
  SUB_LEVEL_AREA_FRACTION: number;
  /**
   * Horizontal padding added to a level's footprint. Room patches cover only
   * room interiors (wall columns yield no intervals), so the raw union stops
   * at the inner wall faces — this allowance extends the footprint so the
   * walls themselves survive footprint-based clipping. Sized for thick
   * exterior walls plus half a grid cell of quantization.
   */
  FOOTPRINT_WALL_PADDING_M: number;

  // -- runtime --
  /** Octree nodes between yields during histogram accumulation. */
  YIELD_NODE_INTERVAL: number;
  /** Columns between yields during extraction stages. */
  YIELD_COLUMN_INTERVAL: number;
  /** Cap on recorded debug surface samples. */
  DEBUG_MAX_SURFACES: number;
}

export const FLOOR_DETECTION_TUNING: FloorDetectionTuning = Object.freeze({
  CELL_SIZE_XY: 0.25,
  BIN_SIZE_Z: 0.05,
  MAX_COLUMNS: 262_144,
  MAX_BINS: 4_096,

  MIN_SURFACE_POINTS: 6,
  SURFACE_PROMINENCE_FACTOR: 3,
  MIN_PEAK_SEPARATION_BINS: 3,

  MIN_ROOM_HEIGHT: 1.9,
  MAX_ROOM_HEIGHT: 6.0,
  FURNITURE_IGNORE_HEIGHT: 1.2,
  CEILING_CLEARANCE: 0.35,
  GAP_MIN_POINT_BUDGET: 8,
  GAP_FRACTION: 0.2,
  INTERMEDIATE_SURFACE_FACTOR: 0.5,
  CANDIDATE_STRENGTH_FACTOR: 0.35,
  MIN_SLAB_SEPARATION: 0.02,

  INTERVAL_OVERLAP_MIN: 0.7,
  MIN_PATCH_AREA_M2: 1.0,
  TRIM_FRACTION: 0.1,
  SLOPED_CEILING_SPREAD: 0.5,

  STOREY_FLOOR_TOLERANCE: 0.08,
  MIN_SLAB_GAP: 0.05,
  MAX_SLAB_GAP: 0.8,
  SUB_LEVEL_AREA_FRACTION: 0.5,
  FOOTPRINT_WALL_PADDING_M: 0.6,

  YIELD_NODE_INTERVAL: 4,
  YIELD_COLUMN_INTERVAL: 2_000,
  DEBUG_MAX_SURFACES: 300_000,
});

/** Merge a partial override (tests, experiments) over the defaults. */
export function resolveTuning(override?: Partial<FloorDetectionTuning>): FloorDetectionTuning {
  return override ? { ...FLOOR_DETECTION_TUNING, ...override } : FLOOR_DETECTION_TUNING;
}
